import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AudiotrackOutlinedIcon from '@mui/icons-material/AudiotrackOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';

import type { LibraryItem, LibraryKind } from '../../domain';
import {
  PRODUCT_LABEL,
  isImportableKind,
  kindLabel,
  libraryFilename,
  libraryItemMeta,
} from './libraryUtils';

interface Props {
  item: LibraryItem;
  /** "Use in Coral" — offered on Lattice images and video only. */
  onImport?: (item: LibraryItem) => void;
  /** This item's import is in flight. */
  importing?: boolean;
  /** Another import is in flight — hold this card's action too. */
  importDisabled?: boolean;
}

const PLACEHOLDER_ICON: Partial<Record<LibraryKind, React.ElementType>> = {
  model3d: ViewInArOutlinedIcon,
  document: DescriptionOutlinedIcon,
  vector: ImageOutlinedIcon,
};

/**
 * One item of the shared SmartAI library, in the gallery's card style: the
 * media itself (img / video / audio) or a typed placeholder, a product badge,
 * and Download — plus "Use in Coral" for Lattice images and video.
 */
export const LibraryCard: React.FC<Props> = ({
  item,
  onImport,
  importing = false,
  importDisabled = false,
}) => {
  const [mediaError, setMediaError] = React.useState(false);
  const canImport = !!onImport && item.product === 'lattice' && isImportableKind(item.kind);
  const title = item.prompt?.trim() || kindLabel(item.kind);
  const meta = libraryItemMeta(item);

  return (
    <Box
      component="article"
      aria-label={title}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1,
        border: theme => `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '1 / 1',
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <LibraryMedia item={item} failed={mediaError} onError={() => setMediaError(true)} />

        <Chip
          label={PRODUCT_LABEL[item.product]}
          size="small"
          sx={{
            position: 'absolute',
            top: 6,
            left: 6,
            height: 20,
            bgcolor: 'background.paper',
            border: theme => `1px solid ${theme.palette.divider}`,
            '& .MuiChip-label': { px: 0.75, fontSize: 10, fontWeight: 600 },
          }}
        />
      </Box>

      <Box sx={{ p: 1 }}>
        <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 500 }}>
          {title}
        </Typography>
        {meta && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {meta}
          </Typography>
        )}
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
          {canImport && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => onImport!(item)}
              disabled={importing || importDisabled}
              startIcon={
                importing ? <CircularProgress size={12} /> : <AddPhotoAlternateOutlinedIcon />
              }
              sx={{ flex: 1, minWidth: 0, py: 0.25, fontSize: 12, textTransform: 'none' }}
            >
              {importing ? 'Adding…' : 'Use in Coral'}
            </Button>
          )}
          {!canImport && <Box sx={{ flex: 1 }} />}
          <Tooltip title="Download">
            <IconButton
              size="small"
              component="a"
              href={item.url}
              download={libraryFilename(item)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download"
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
};

const LibraryMedia: React.FC<{ item: LibraryItem; failed: boolean; onError: () => void }> = ({
  item,
  failed,
  onError,
}) => {
  const fill = { width: '100%', height: '100%', objectFit: 'cover' } as const;

  if (!failed && item.kind === 'image') {
    return (
      <Box
        component="img"
        src={item.thumbnailUrl ?? item.url}
        alt=""
        loading="lazy"
        onError={onError}
        sx={fill}
      />
    );
  }

  if (!failed && item.kind === 'video') {
    return (
      <Box
        component="video"
        src={item.url}
        poster={item.thumbnailUrl ?? undefined}
        controls
        preload="metadata"
        playsInline
        onError={onError}
        sx={{ ...fill, bgcolor: 'common.black' }}
      />
    );
  }

  if (!failed && item.kind === 'audio') {
    return (
      <Stack spacing={1} alignItems="center" sx={{ width: '100%', px: 1 }}>
        <AudiotrackOutlinedIcon sx={{ fontSize: 40, opacity: 0.4 }} />
        <Box component="audio" src={item.url} controls preload="none" sx={{ width: '100%' }} />
      </Stack>
    );
  }

  // Thumbnail-able kinds (vector, model3d with a render) show the thumbnail;
  // everything else gets a typed placeholder with the prompt.
  if (!failed && item.thumbnailUrl) {
    return (
      <Box
        component="img"
        src={item.thumbnailUrl}
        alt=""
        loading="lazy"
        onError={onError}
        sx={fill}
      />
    );
  }

  const Icon = PLACEHOLDER_ICON[item.kind] ?? InsertDriveFileOutlinedIcon;
  return (
    <Stack
      spacing={0.75}
      sx={{ width: '100%', height: '100%', p: 1.25, pt: 4, justifyContent: 'flex-start' }}
      aria-label={`${kindLabel(item.kind)} preview`}
    >
      <Icon sx={{ fontSize: 28, opacity: 0.5 }} />
      <Typography variant="caption" fontWeight={600}>
        {kindLabel(item.kind)}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: 1.35,
        }}
      >
        {item.prompt?.trim() || 'No prompt'}
      </Typography>
    </Stack>
  );
};
